import type { DealValuation } from '@app/ai';
import type { ActivityCategory, CompensationType } from '@app/validators';

// US-029: shape of a compensation item as returned by `deal.get`. Mirrors
// the router boundary mapping; Decimals come through as numbers.
interface CompensationItemView {
  id: string;
  type: CompensationType;
  amount: number;
  currency: string;
  description: string;
  unitLabel: string | null;
  royaltyPercent: number | null;
  expectedVolumeLow: number | null;
  expectedVolumeHigh: number | null;
  dueDate: string | null;
  orderIndex: number;
}

function compensationTypeLabel(type: CompensationType): string {
  switch (type) {
    case 'CASH':
      return 'Cash';
    case 'GOODS_SERVICES':
      return 'Goods & Services';
    case 'BONUS':
      return 'Bonus';
    case 'ROYALTY':
      return 'Royalty';
  }
}

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  StatusBadge,
  toast,
} from '@app/ui';
import {
  AnimatePresenceWrapper,
  PageTransition,
  StaggerContainer,
  StaggerItem,
} from '@app/ui/motion';
import {
  ACTIVITY_CATEGORY_DEFAULTS,
  ACTIVITY_METADATA_SCHEMAS,
  ALLOWED_UPLOAD_ACCEPT,
  formatCurrency,
  formatDate,
  isAllowedUploadType,
  MAX_UPLOAD_SIZE_BYTES,
} from '@app/validators';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { DealEditForm } from '../../../components/deal-edit-form';
import {
  DeliverableEditor,
  type DeliverableEditorInitial,
} from '../../../components/deliverable-editor';
import { NilGoReadinessPanel } from '../../../components/nil-go-readiness-panel';
import { SubmissionHistoryPanel } from '../../../components/submission-history-panel';
import { SubmissionPreviewModal } from '../../../components/submission-preview-modal';
import { api, client } from '../../../lib/api';
import { handleFeatureGateError } from '../../../lib/feature-gate-client';

// US-030 client-side readiness — keeps the badge instant without
// per-row API calls. Mirrors `computeDeliverableReadiness` on the server.
function clientReadiness(d: {
  status: string;
  activityCategory: ActivityCategory | null;
  metadata: string | null;
  skipProofReason: string | null;
  proofs?: { id: string }[];
}): 'ok' | 'needs_detail' | 'needs_proof' {
  if (d.status === 'CANCELLED') return 'ok';
  if (!d.activityCategory) return 'needs_detail';
  const schema = ACTIVITY_METADATA_SCHEMAS[d.activityCategory];
  let meta: Record<string, unknown> = {};
  if (d.metadata) {
    try {
      const parsed = JSON.parse(d.metadata);
      if (parsed && typeof parsed === 'object') meta = parsed as Record<string, unknown>;
    } catch {
      // fall through to schema validation against empty obj
    }
  }
  if (!schema.safeParse(meta).success) return 'needs_detail';
  if (d.status === 'COMPLETED' && (d.proofs?.length ?? 0) === 0 && !d.skipProofReason) {
    return 'needs_proof';
  }
  return 'ok';
}

function ReadinessBadge({ status }: { status: 'ok' | 'needs_detail' | 'needs_proof' }) {
  const styles = {
    ok: { className: 'bg-green-100 text-green-800 border-green-200', label: 'Ready' },
    needs_detail: {
      className: 'bg-amber-100 text-amber-800 border-amber-200',
      label: 'Needs detail',
    },
    needs_proof: {
      className: 'bg-amber-100 text-amber-800 border-amber-200',
      label: 'Needs proof',
    },
  } as const;
  const s = styles[status];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${s.className}`}
    >
      {s.label}
    </span>
  );
}

export const Route = createFileRoute('/_authenticated/deals/$dealId')({
  loader: async ({ context, params }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.deal.get.queryOptions({ input: { id: params.dealId } }),
    );
  },
  pendingComponent: () => (
    <div className="animate-pulse space-y-4">
      <div className="h-8 bg-muted rounded w-48" />
      <div className="h-64 bg-muted rounded" />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="text-center py-12">
      <p className="text-red-600">
        Failed to load deal: {error instanceof Error ? error.message : 'Something went wrong'}
      </p>
      <Link to="/deals" className="text-sm underline mt-2 inline-block">
        Back to deals
      </Link>
    </div>
  ),
  component: DealDetail,
});

function DealDetail() {
  const { dealId } = Route.useParams();
  const { data: deal } = useSuspenseQuery(api.deal.get.queryOptions({ input: { id: dealId } }));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [valuation, setValuation] = useState<DealValuation | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showDeliverableForm, setShowDeliverableForm] = useState(false);
  // Full edit-form open state — replaces the read-only Deal Information +
  // Compensation cards while editing.
  const [editingDeal, setEditingDeal] = useState(false);
  // US-033: NIL Go submit modal open state
  const [nilGoSubmitOpen, setNilGoSubmitOpen] = useState(false);
  // US-030: per-row inline edit / proof states
  const [editingDeliverableId, setEditingDeliverableId] = useState<string | null>(null);
  const [proofForId, setProofForId] = useState<string | null>(null);
  const [proofUrl, setProofUrl] = useState('');
  const [proofKind, setProofKind] = useState<
    'link' | 'screenshot' | 'metrics_screenshot' | 'document'
  >('link');
  const [skipMode, setSkipMode] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  const invalidateDeal = () =>
    queryClient.invalidateQueries({
      queryKey: api.deal.get.queryKey({ input: { id: dealId } }),
    });

  // Bust every deal-namespaced query: list (filtered + paginated), dashboard,
  // and `activeCount` (drives the plan-cap gate on /deals). Status changes
  // (COMPLETED/CANCELLED/EXPIRED) shift the active count; deletes also do.
  // Without this, the "New Deal" button can keep routing to /pricing for up
  // to staleTime + the next focus event after a deal closes.
  //
  // US-033.3: also bust `nilGo` (readiness) and `payor.ytdExposure` — these
  // depend on the deal's compensation total + signedDate but live under
  // different paths, so without explicit invalidation the readiness panel
  // and YTD progress bar keep showing stale numbers after a deal edit.
  const invalidateAllDealQueries = () =>
    queryClient.invalidateQueries({
      // oRPC queryKey is [path[], options]; queryKey[0] is the path array.
      predicate: (query) => {
        const path = query.queryKey?.[0];
        if (!Array.isArray(path)) return false;
        if (path[0] === 'deal') return true;
        if (path[0] === 'nilGo') return true;
        if (path[0] === 'payor' && path[1] === 'ytdExposure') return true;
        return false;
      },
    });

  const updateMutation = useMutation({
    ...api.deal.update.mutationOptions(),
    onSuccess: () => {
      invalidateDeal();
      invalidateAllDealQueries();
    },
  });

  // D9: athlete confirms an agent-created draft, flipping it to ACTIVE.
  const confirmDraftMutation = useMutation({
    ...api.deal.confirmAgentDraft.mutationOptions(),
    onSuccess: () => {
      toast.success('Deal confirmed — it now counts as active.');
      invalidateDeal();
      invalidateAllDealQueries();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not confirm the deal'),
  });

  const deleteMutation = useMutation({
    ...api.deal.delete.mutationOptions(),
    onSuccess: () => {
      invalidateAllDealQueries();
      navigate({ to: '/deals' });
    },
  });

  const addPaymentMutation = useMutation({
    ...api.deal.addPayment.mutationOptions(),
    onSuccess: () => {
      setShowPaymentForm(false);
      invalidateDeal();
    },
  });

  // US-030: structured deliverable mutations. The legacy
  // `deal.addDeliverable` / `deal.toggleDeliverable` endpoints stay around
  // for back-compat, but this page exclusively talks to the new router.
  //
  // All of these use `invalidateAllDealQueries` (not the narrower
  // `invalidateDeal`) because adding/editing/completing a deliverable
  // also changes the NIL Go readiness verdict — without busting the
  // `nilGo` namespace, the readiness checklist keeps saying "add a
  // deliverable" after one was just added.
  const createDeliverableMutation = useMutation({
    ...api.deliverable.create.mutationOptions(),
    onSuccess: () => {
      setShowDeliverableForm(false);
      setEditingDeliverableId(null);
      invalidateAllDealQueries();
    },
  });

  const updateDeliverableMutation = useMutation({
    ...api.deliverable.update.mutationOptions(),
    onSuccess: () => {
      setEditingDeliverableId(null);
      invalidateAllDealQueries();
    },
  });

  const markCompletedMutation = useMutation({
    ...api.deliverable.markCompleted.mutationOptions(),
    onSuccess: () => {
      setProofForId(null);
      invalidateAllDealQueries();
    },
  });

  const reopenDeliverableMutation = useMutation({
    ...api.deliverable.reopen.mutationOptions(),
    onSuccess: invalidateAllDealQueries,
  });

  const addProofMutation = useMutation({
    ...api.deliverable.addProof.mutationOptions(),
    onSuccess: () => {
      setProofForId(null);
      invalidateAllDealQueries();
    },
  });

  const removeProofMutation = useMutation({
    ...api.deliverable.removeProof.mutationOptions(),
    onSuccess: invalidateAllDealQueries,
  });

  const fileDeleteMutation = useMutation({
    ...api.file.delete.mutationOptions(),
    onSuccess: invalidateAllDealQueries,
  });

  const handleEstimate = async () => {
    setEstimating(true);
    try {
      const result = await client.ai.estimateDealValue({ dealId: deal.id });
      setValuation(result);
    } catch (error) {
      // FEATURE_GATE responses redirect to /pricing; only toast non-gate errors.
      if (handleFeatureGateError(error)) return;
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      toast.error(`Estimation failed: ${message}`);
    } finally {
      setEstimating(false);
    }
  };

  const handleStatusChange = (status: 'DRAFT' | 'ACTIVE' | 'COMPLETED') => {
    updateMutation.mutate({ id: deal.id, status });
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleAddPayment = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    addPaymentMutation.mutate({
      dealId: deal.id,
      amount: Number(fd.get('amount')),
      dueDate: (fd.get('dueDate') as string) || undefined,
      method: (fd.get('method') as string) || undefined,
      notes: (fd.get('notes') as string) || undefined,
    });
  };

  // US-030 handlers
  const openProofFor = (deliverableId: string) => {
    setProofForId(deliverableId);
    setProofUrl('');
    setProofKind('link');
    setSkipMode(false);
    setSkipReason('');
  };

  const handleSubmitProof = () => {
    if (!proofForId) return;
    const d = deal.deliverables.find((x) => x.id === proofForId);
    if (!d) return;

    if (skipMode) {
      if (!skipReason.trim()) {
        toast.error('Add a reason for skipping the proof');
        return;
      }
      markCompletedMutation.mutate({ id: proofForId, skipProofReason: skipReason.trim() });
      return;
    }

    if (!proofUrl.trim()) {
      toast.error('Paste a URL or upload a file first');
      return;
    }

    // If the deliverable isn't COMPLETED yet, this proof completes it.
    // Otherwise just append a proof to the existing record.
    if (d.status === 'COMPLETED') {
      addProofMutation.mutate({
        deliverableId: proofForId,
        proof: { type: proofKind, url: proofUrl.trim() },
      });
    } else {
      markCompletedMutation.mutate({
        id: proofForId,
        proof: { type: proofKind, url: proofUrl.trim() },
      });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Client-side pre-check against the same allowlist the server enforces
    // (SSOT in @app/validators/uploads). Without this, an unsupported pick
    // round-trips to Vercel Blob and comes back as a cryptic
    // "Content type mismatch" error AFTER the upload has started.
    if (!isAllowedUploadType(file)) {
      toast.error(`File type not allowed: ${file.type || 'unknown'}`);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(`File too large (max ${Math.round(MAX_UPLOAD_SIZE_BYTES / 1_048_576)} MB)`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    // Capture file count BEFORE upload. Vercel Blob's addRandomSuffix:true
    // (the default) mangles uploaded filenames (deals/X/file.pdf →
    // deals/X/file-aBcDe.pdf), so client-side filename equality matching
    // against the webhook-stored fileName is unreliable. Polling on count
    // delta is robust against random suffixes and any future filename
    // transforms.
    const beforeCount = deal.files?.length ?? 0;
    try {
      // Upload directly to Vercel Blob (bypasses 4.5MB serverless body limit).
      // The server registers the file in the DB via the onUploadCompleted
      // webhook using a Vercel-signed tokenPayload — clients no longer
      // supply URLs (prevents cross-user blob deletion).
      const { upload } = await import('@vercel/blob/client');
      await upload(`deals/${deal.id}/${file.name}`, file, {
        access: 'public',
        handleUploadUrl: '/api/upload',
        clientPayload: JSON.stringify({
          dealId: deal.id,
          category: 'document',
          fileSize: file.size,
        }),
      });

      // The onUploadCompleted webhook is delivered out-of-band by Vercel
      // Blob, so the file may not appear immediately after upload() resolves.
      // Poll the deal query until the file count increases (or we time out).
      const startTime = Date.now();
      const timeoutMs = 10_000;
      let registered = false;
      while (Date.now() - startTime < timeoutMs) {
        await new Promise((r) => setTimeout(r, 500));
        await invalidateDeal();
        const fresh = await queryClient.fetchQuery(
          api.deal.get.queryOptions({ input: { id: deal.id } }),
        );
        if ((fresh.files?.length ?? 0) > beforeCount) {
          registered = true;
          break;
        }
      }

      if (registered) {
        toast.success('File uploaded');
      } else {
        // Webhook hasn't landed in the budget window — surface a softer
        // message and trigger one more invalidation so the file appears
        // whenever the webhook eventually fires.
        toast('Upload complete — file may take a moment to appear', { duration: 5000 });
        invalidateDeal();
      }
    } catch (error) {
      console.error(error);
      toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
      // Reset file input so re-selecting the same file triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = (fileId: string) => {
    setFileToDelete(fileId);
  };

  return (
    <PageTransition>
      <div className="mb-6">
        <Link to="/deals" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; Back to Deals
        </Link>
      </div>

      {/* D9: an agent created this deal and it stays a draft until the
          athlete confirms. Only the athlete sees this — `deal.get` scopes
          to their own deals, and the confirm mutation does too, so an
          agent can never self-confirm. */}
      {deal.awaitingConfirmation && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <p className="font-medium text-blue-900">Your agency added this deal for you</p>
            <p className="text-sm text-blue-900/80 mt-1">
              Review the terms below. It stays a draft — and won't count toward your active deals
              or NIL Go reporting — until you confirm it.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={() => confirmDraftMutation.mutate({ id: deal.id })}
                disabled={confirmDraftMutation.isPending}
              >
                {confirmDraftMutation.isPending ? 'Confirming…' : 'Confirm this deal'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingDeal(true)}
                disabled={confirmDraftMutation.isPending}
              >
                Edit first
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{deal.title}</h1>
          <p className="text-muted-foreground text-sm">{deal.brandName}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="min-h-[44px]"
            onClick={() => setEditingDeal((v) => !v)}
          >
            {editingDeal ? 'Close Edit' : 'Edit Deal'}
          </Button>
          <Button
            variant="outline"
            className="min-h-[44px]"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ALLOWED_UPLOAD_ACCEPT}
            onChange={handleFileUpload}
          />
          <Button
            variant="outline"
            className="min-h-[44px] text-red-600 hover:text-red-700"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </div>

      <StaggerContainer className="grid lg:grid-cols-3 gap-6">
        {/* Deal Info OR edit form */}
        <StaggerItem className="lg:col-span-2">
          {editingDeal ? (
            <DealEditForm
              deal={{
                id: deal.id,
                title: deal.title,
                brandName: deal.brandName,
                brandContact: deal.brandContact ?? null,
                description: deal.description ?? null,
                source: deal.source ?? null,
                startDate: deal.startDate ?? null,
                endDate: deal.endDate ?? null,
                signedDate: (deal as { signedDate?: string | null }).signedDate ?? null,
                isExclusive: deal.isExclusive,
                payor: (deal as { payor?: Parameters<typeof DealEditForm>[0]['deal']['payor'] }).payor ?? null,
                compensationItems: (deal.compensationItems ?? []) as Parameters<
                  typeof DealEditForm
                >[0]['deal']['compensationItems'],
              }}
              onCancel={() => setEditingDeal(false)}
              onSaved={() => {
                setEditingDeal(false);
                invalidateDeal();
                invalidateAllDealQueries();
              }}
            />
          ) : (
            <>
          <Card>
            <CardHeader>
              <CardTitle>Deal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Amount</dt>
                  <dd className="font-medium text-lg">{formatCurrency(deal.amount)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Brand Contact</dt>
                  <dd>{deal.brandContact || '--'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Start Date</dt>
                  <dd>{deal.startDate ? formatDate(deal.startDate) : '--'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">End Date</dt>
                  <dd>{deal.endDate ? formatDate(deal.endDate) : '--'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Source</dt>
                  <dd>{deal.source || '--'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Exclusive</dt>
                  <dd>{deal.isExclusive ? 'Yes' : 'No'}</dd>
                </div>
                {deal.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">Description</dt>
                    <dd className="whitespace-pre-wrap">{deal.description}</dd>
                  </div>
                )}
                {deal.aiExtracted && (
                  <div className="sm:col-span-2">
                    <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-800 rounded-full px-2 py-0.5">
                      AI Extracted{' '}
                      {deal.aiConfidence
                        ? `(${Math.round(deal.aiConfidence * 100)}% confidence)`
                        : ''}
                    </span>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* US-029: structured compensation breakdown. Read-only for v1;
              inline editing lives on the deal-create form for now. */}
          {deal.compensationItems && deal.compensationItems.length > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Compensation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {deal.compensationItems.map((c: CompensationItemView) => (
                    <li
                      key={c.id}
                      className="flex items-start justify-between gap-3 border-b last:border-b-0 pb-2 last:pb-0"
                    >
                      <div className="min-w-0">
                        <span className="text-xs uppercase text-muted-foreground tracking-wide">
                          {compensationTypeLabel(c.type)}
                        </span>
                        <p className="text-sm">{c.description}</p>
                        {c.type === 'ROYALTY' && c.royaltyPercent !== null && (
                          <p className="text-xs text-muted-foreground">
                            {c.royaltyPercent}%{c.unitLabel ? ` ${c.unitLabel}` : ''}
                            {c.expectedVolumeLow !== null
                              ? ` · est. ${c.expectedVolumeLow}${
                                  c.expectedVolumeHigh !== null ? `–${c.expectedVolumeHigh}` : ''
                                }`
                              : ''}
                          </p>
                        )}
                      </div>
                      <span className="font-medium whitespace-nowrap">
                        {formatCurrency(c.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between mt-3 pt-2 border-t text-sm">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-semibold">{formatCurrency(deal.amount)}</span>
                </div>
              </CardContent>
            </Card>
          )}
            </>
          )}
        </StaggerItem>

        {/* Status & Actions */}
        <StaggerItem className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <StatusBadge status={deal.status} size="md" />
              <div className="flex flex-wrap gap-2 mt-3">
                {deal.status === 'DRAFT' && (
                  <Button size="sm" onClick={() => handleStatusChange('ACTIVE')}>
                    Activate
                  </Button>
                )}
                {deal.status === 'ACTIVE' && (
                  <Button size="sm" onClick={() => handleStatusChange('COMPLETED')}>
                    Complete
                  </Button>
                )}
                {deal.status === 'COMPLETED' && (
                  <Button size="sm" variant="outline" onClick={() => handleStatusChange('ACTIVE')}>
                    Reopen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>AI Valuation</CardTitle>
              <CardDescription>Get an AI estimate of this deal's fair market value</CardDescription>
            </CardHeader>
            <CardContent>
              <AnimatePresenceWrapper show={!!valuation}>
                {valuation && (
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-muted-foreground text-xs">Low</p>
                        <p className="font-medium">
                          {formatCurrency(valuation.estimatedValue.low)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Mid</p>
                        <p className="font-semibold text-lg">
                          {formatCurrency(valuation.estimatedValue.mid)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">High</p>
                        <p className="font-medium">
                          {formatCurrency(valuation.estimatedValue.high)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Score</span>
                      <span className="font-medium">{valuation.score}/10</span>
                    </div>
                    <div>
                      <RecommendationBadge rec={valuation.recommendation} />
                    </div>
                    <p className="text-muted-foreground">{valuation.reasoning}</p>
                    {valuation.negotiationTips && valuation.negotiationTips.length > 0 && (
                      <div>
                        <p className="font-medium mb-1">Negotiation Tips:</p>
                        <ul className="list-disc pl-4 text-muted-foreground space-y-1">
                          {valuation.negotiationTips.map((tip, i) => (
                            <li key={i}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </AnimatePresenceWrapper>
              {!valuation && (
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={handleEstimate}
                  disabled={estimating}
                >
                  {estimating ? 'Estimating...' : 'Estimate Value'}
                </Button>
              )}
            </CardContent>
          </Card>
        </StaggerItem>
      </StaggerContainer>

      {/* US-033: NIL Go readiness + submit */}
      <div className="mt-6 space-y-4">
        <NilGoReadinessPanel
          dealId={deal.id}
          onSubmitClick={() => {
            setNilGoSubmitOpen(true);
          }}
        />
        {/* US-033.4: decision banner + revisions history. Renders nothing
            when the deal has zero submissions; once there's at least one,
            shows the latest decision banner + a per-revision history. */}
        <SubmissionHistoryPanel
          dealId={deal.id}
          onResubmitClick={() => setNilGoSubmitOpen(true)}
        />
      </div>
      {nilGoSubmitOpen && (
        <SubmissionPreviewModal
          dealId={deal.id}
          open={nilGoSubmitOpen}
          onClose={() => setNilGoSubmitOpen(false)}
        />
      )}

      {/* Payments & Deliverables */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Payments</CardTitle>
            <CardDescription>Track expected and received payments</CardDescription>
          </CardHeader>
          <CardContent>
            {deal.payments.length > 0 ? (
              <div className="space-y-2 mb-4">
                {deal.payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between py-2 border-b last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-medium">{formatCurrency(p.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.dueDate ? `Due ${formatDate(p.dueDate)}` : 'No due date'}
                        {p.method && ` via ${p.method}`}
                      </p>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm mb-4">No payments logged yet.</p>
            )}

            <AnimatePresenceWrapper show={showPaymentForm}>
              <form onSubmit={handleAddPayment} className="space-y-3 border-t pt-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input name="amount" type="number" step="0.01" required placeholder="0.00" />
                  </div>
                  <div>
                    <Label className="text-xs">Due Date</Label>
                    <Input name="dueDate" type="date" />
                  </div>
                </div>
                <Input name="method" placeholder="Payment method (optional)" />
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowPaymentForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </AnimatePresenceWrapper>
            {!showPaymentForm && (
              <Button variant="outline" size="sm" onClick={() => setShowPaymentForm(true)}>
                Add Payment
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Deliverables</CardTitle>
            <CardDescription>What you owe to the brand — categorized for NIL Go</CardDescription>
          </CardHeader>
          <CardContent>
            {deal.deliverables.length > 0 ? (
              <div className="space-y-3 mb-4">
                {deal.deliverables.map((d) => {
                  const cat = d.activityCategory as ActivityCategory | null;
                  const catMeta = cat ? ACTIVITY_CATEGORY_DEFAULTS[cat] : null;
                  const readiness = clientReadiness({
                    status: d.status,
                    activityCategory: cat,
                    metadata: (d as { metadata?: string | null }).metadata ?? null,
                    skipProofReason:
                      (d as { skipProofReason?: string | null }).skipProofReason ?? null,
                    proofs: (d as { proofs?: { id: string }[] }).proofs ?? [],
                  });
                  const proofs =
                    (
                      d as {
                        proofs?: { id: string; type: string; url: string; capturedAt: string }[];
                      }
                    ).proofs ?? [];
                  const isEditing = editingDeliverableId === d.id;
                  const isProofOpen = proofForId === d.id;

                  return (
                    <div key={d.id} className="border rounded-md">
                      <div className="flex items-start gap-3 p-3">
                        <span
                          aria-hidden
                          className="text-2xl shrink-0"
                          title={catMeta?.label ?? ''}
                        >
                          {catMeta?.emoji ?? '📋'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p
                              className={`text-sm font-medium ${d.status === 'COMPLETED' ? 'line-through text-muted-foreground' : ''}`}
                            >
                              {d.title}
                            </p>
                            <ReadinessBadge status={readiness} />
                            {d.status === 'COMPLETED' && (
                              <span className="text-xs text-muted-foreground">Completed</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {[
                              catMeta?.label,
                              d.platform,
                              d.dueDate ? `Due ${formatDate(d.dueDate)}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || 'No details'}
                          </p>
                          {proofs.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {proofs.map((p) => (
                                <div key={p.id} className="flex items-center gap-2 text-xs">
                                  <span aria-hidden>📎</span>
                                  <a
                                    href={p.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline truncate max-w-xs"
                                  >
                                    {p.url}
                                  </a>
                                  <span className="text-muted-foreground">
                                    {formatDate(p.capturedAt)}
                                  </span>
                                  <button
                                    type="button"
                                    className="text-muted-foreground hover:text-red-600"
                                    onClick={() => removeProofMutation.mutate({ id: p.id })}
                                    aria-label="Remove proof"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {(d as { skipProofReason?: string | null }).skipProofReason && (
                            <p className="mt-2 text-xs text-amber-700">
                              Proof skipped: {(d as { skipProofReason?: string }).skipProofReason}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          {d.status !== 'COMPLETED' ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openProofFor(d.id)}
                            >
                              Mark complete
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => reopenDeliverableMutation.mutate({ id: d.id })}
                            >
                              Reopen
                            </Button>
                          )}
                          {d.status === 'COMPLETED' && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openProofFor(d.id)}
                            >
                              Add proof
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingDeliverableId(isEditing ? null : d.id)}
                          >
                            {isEditing ? 'Close' : 'Edit'}
                          </Button>
                        </div>
                      </div>

                      <AnimatePresenceWrapper show={isProofOpen}>
                        <div className="border-t bg-muted/30 p-3 space-y-2">
                          <div className="flex items-center gap-3 text-xs">
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                checked={!skipMode}
                                onChange={() => setSkipMode(false)}
                              />
                              Attach proof
                            </label>
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                checked={skipMode}
                                onChange={() => setSkipMode(true)}
                              />
                              Skip with reason
                            </label>
                          </div>
                          {!skipMode ? (
                            <>
                              <div className="grid grid-cols-3 gap-2">
                                <select
                                  className="flex h-10 col-span-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  value={proofKind}
                                  onChange={(e) => setProofKind(e.target.value as typeof proofKind)}
                                >
                                  <option value="link">Link</option>
                                  <option value="screenshot">Screenshot</option>
                                  <option value="metrics_screenshot">Metrics screenshot</option>
                                  <option value="document">Document</option>
                                </select>
                                <Input
                                  className="col-span-2"
                                  placeholder="Paste URL"
                                  value={proofUrl}
                                  onChange={(e) => setProofUrl(e.target.value)}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Paste the post URL, screenshot link, or doc URL. Direct uploads go
                                through the Files section above.
                              </p>
                            </>
                          ) : (
                            <Input
                              placeholder="Why are you skipping the proof? (logged for audit)"
                              value={skipReason}
                              onChange={(e) => setSkipReason(e.target.value)}
                            />
                          )}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSubmitProof}
                              disabled={
                                markCompletedMutation.isPending || addProofMutation.isPending
                              }
                            >
                              {d.status === 'COMPLETED' ? 'Add proof' : 'Mark complete'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setProofForId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </AnimatePresenceWrapper>

                      <AnimatePresenceWrapper show={isEditing}>
                        <div className="border-t bg-muted/20 p-3">
                          <DeliverableEditor
                            initial={
                              {
                                id: d.id,
                                title: d.title,
                                description: d.description,
                                activityCategory: cat,
                                type: d.type,
                                platform: d.platform,
                                dueDate: d.dueDate,
                                metadata: (() => {
                                  const raw = (d as { metadata?: string | null }).metadata;
                                  if (!raw) return null;
                                  try {
                                    return JSON.parse(raw) as Record<string, unknown>;
                                  } catch {
                                    return null;
                                  }
                                })(),
                              } satisfies DeliverableEditorInitial
                            }
                            saving={updateDeliverableMutation.isPending}
                            onCancel={() => setEditingDeliverableId(null)}
                            onSave={async (input) => {
                              await updateDeliverableMutation.mutateAsync({
                                id: d.id,
                                patch: input,
                              });
                            }}
                          />
                        </div>
                      </AnimatePresenceWrapper>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm mb-4">No deliverables added yet.</p>
            )}

            <AnimatePresenceWrapper show={showDeliverableForm}>
              <DeliverableEditor
                saving={createDeliverableMutation.isPending}
                onCancel={() => setShowDeliverableForm(false)}
                onSave={async (input) => {
                  await createDeliverableMutation.mutateAsync({
                    dealId: deal.id,
                    deliverable: input,
                  });
                }}
              />
            </AnimatePresenceWrapper>
            {!showDeliverableForm && (
              <Button variant="outline" size="sm" onClick={() => setShowDeliverableForm(true)}>
                Add Deliverable
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Files */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Files</CardTitle>
          <CardDescription>Contracts, screenshots, and receipts</CardDescription>
        </CardHeader>
        <CardContent>
          {deal.files.length > 0 ? (
            <div className="space-y-2">
              {deal.files.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center justify-between py-2 border-b last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-xs">
                      {f.fileType.split('/')[1]?.toUpperCase().slice(0, 3) || 'FILE'}
                    </div>
                    <div>
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium hover:underline"
                      >
                        {f.fileName}
                      </a>
                      <p className="text-xs text-muted-foreground">
                        {(f.fileSize / 1024).toFixed(0)} KB &middot; {formatDate(f.createdAt)}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteFile(f.id)}>
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No files uploaded yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Delete Deal Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this deal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate({ id: deal.id })}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete File Dialog */}
      <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this file?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (fileToDelete) {
                  fileDeleteMutation.mutate({ fileId: fileToDelete });
                  setFileToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageTransition>
  );
}

function RecommendationBadge({ rec }: { rec: string }) {
  const styles: Record<string, string> = {
    strong_accept: 'bg-green-100 text-green-800',
    accept: 'bg-blue-100 text-blue-800',
    negotiate: 'bg-yellow-100 text-yellow-800',
    decline: 'bg-red-100 text-red-800',
  };
  const labels: Record<string, string> = {
    strong_accept: 'Strong Accept',
    accept: 'Accept',
    negotiate: 'Negotiate',
    decline: 'Decline',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs ${styles[rec] ?? 'bg-muted text-muted-foreground'}`}
    >
      {labels[rec] ?? rec}
    </span>
  );
}
